import { useEffect, useState } from "react";
import { CheckCircle2, Volume2, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { pickVoice, voicesReady, speak } from "@/lib/speech";

const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isSamsung = () => /SM-|Samsung/i.test(navigator.userAgent);

const ANDROID_STEPS = [
  <>Installe ou mets à jour <b>« Services vocaux de Google »</b> : <a className="text-blue-600 underline" href="https://play.google.com/store/apps/details?id=com.google.android.tts" target="_blank" rel="noreferrer" data-testid="fr-voice-playstore-link">ouvrir le Play Store</a>.</>,
  <>Ouvre <b>Paramètres</b> › {isSamsung() ? <b>Gestion globale › Langue</b> : <b>Système › Langues et saisie</b>} › <b>Synthèse vocale</b> (sortie de synthèse vocale).</>,
  <>Moteur préféré : choisis <b>Services vocaux de Google</b>.</>,
  <>Touche la roue ⚙️ à côté › <b>Installer les données vocales</b> › <b>Français (France)</b> › télécharger.</>,
  <>Ferme complètement Chrome, rouvre anouappran.com, puis touche « Tester la voix » ci-dessous.</>,
];
const IOS_STEPS = [
  <>Ouvre <b>Réglages</b> › <b>Accessibilité</b> › <b>Contenu énoncé</b> › <b>Voix</b>.</>,
  <>Choisis <b>Français</b> › <b>France</b>, puis télécharge une voix (par ex. « Amélie »).</>,
  <>Ferme Safari ou Chrome, rouvre anouappran.com, puis touche « Tester la voix ».</>,
];

// Shown once per session when the phone has no French voice: free steps to install one.
export default function FrVoiceHelp() {
  const [open, setOpen] = useState(false);
  const [test, setTest] = useState(null);
  useEffect(() => {
    const show = () => { if (localStorage.getItem("aa_fr_voice_help_off") !== "1") setOpen(true); };
    window.addEventListener("aa-no-fr-voice", show);
    return () => window.removeEventListener("aa-no-fr-voice", show);
  }, []);
  const runTest = async () => {
    await voicesReady(3000);
    const ok = !!pickVoice("fr");
    setTest(ok);
    if (ok) speak("Bonjour ! Moi c'est Kiko. La voix française marche bien.", "fr");
  };
  const steps = isIOS() ? IOS_STEPS : ANDROID_STEPS;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="fr-voice-help">
        <DialogHeader>
          <DialogTitle>La voix française n'est pas installée</DialogTitle>
          <DialogDescription>
            Ce téléphone n'a pas encore de voix française, alors une voix de secours avec un accent anglais lit les questions.
            C'est gratuit de l'installer et ça prend 2 minutes :
          </DialogDescription>
        </DialogHeader>
        <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700" data-testid="fr-voice-steps">
          {steps.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
        {test !== null && (
          <p className={`flex items-center gap-2 text-sm font-semibold ${test ? "text-green-700" : "text-red-600"}`} data-testid="fr-voice-test-result">
            {test ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
            {test ? "Voix française trouvée ! Tout est prêt." : "Toujours pas de voix française. Vérifie les étapes, puis rouvre Chrome."}
          </p>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button onClick={runTest} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="fr-voice-test-btn">
            <Volume2 size={16} className="mr-1" /> Tester la voix
          </Button>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full" data-testid="fr-voice-close-btn">Plus tard</Button>
          <Button variant="ghost" onClick={() => { localStorage.setItem("aa_fr_voice_help_off", "1"); setOpen(false); }}
            className="rounded-full text-slate-500" data-testid="fr-voice-never-btn">Ne plus afficher</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
